import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Lightbulb,
  ListChecks,
  Loader2,
  Sparkles,
  Tag,
  User,
  X,
} from "lucide-react";
import { createInitiativeItem, INITIATIVE_LIST_ID } from "../api/graph";
import type { NewInitiativeFields } from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import { useListColumns, choicesFor, hintFor } from "../hooks/useListColumns";
import { useInitiativeAssist } from "../hooks/useInitiativeAssist";
import type { FieldEvaluation } from "../hooks/useInitiativeAssist";
import { useProfile } from "../hooks/useProfile";
import type { InitiativeItem } from "../types/navigator";
import { ConfirmDialog } from "./ConfirmDialog";

type Props = {
  /** Existing initiative items — used to suggest choice values for dropdowns
   *  so we don't have to hardcode them. */
  existing: InitiativeItem[];
  onClose: () => void;
  onCreated: () => void;
  /** Optional — switches the parent to the experimental chat-style
   *  submission flow instead of this form. Omitted entirely hides the
   *  entry point, so this stays a no-op everywhere that doesn't opt in. */
  onTryChat?: () => void;
};

type FormState = {
  Title: string;
  Yourname: string;
  Team: string;
  Region: string;
  Problem: string;
  Frequency: string;
  ImpactedToday: string;
  IfSolved: string;
  AnticipatedImpact: string;
  // "What type of impact" is a multi-select choice column — track selections
  // as an array of labels.
  ImpactType: string[];
  ProposedSolution: string;
  ExistingExamples: string;
  DataInputs: string;
  ConstraintsRisks: string;
  AnythingElse: string;
  Sponsor: string;
};

const EMPTY: FormState = {
  Title: "",
  Yourname: "",
  Team: "",
  Region: "",
  Problem: "",
  Frequency: "",
  ImpactedToday: "",
  IfSolved: "",
  AnticipatedImpact: "",
  ImpactType: [],
  ProposedSolution: "",
  ExistingExamples: "",
  DataInputs: "",
  ConstraintsRisks: "",
  AnythingElse: "",
  Sponsor: "",
};

// The descriptive fields the AI completeness review judges for substance —
// the short/choice fields (name, team, region, frequency, impact type)
// don't need it, just a fill check.
const FIELD_LABELS: Partial<Record<keyof FormState, string>> = {
  Problem: "What problem are you trying to solve?",
  ImpactedToday: "Who's impacted? Who would benefit?",
  IfSolved: "If this problem were solved, what would success look like?",
  AnticipatedImpact: "Anticipated impact",
  ProposedSolution: "Do you have a proposed AI solution in mind?",
  ExistingExamples: "Are there existing examples we could mirror or learn from?",
  DataInputs: "What data or inputs would be involved?",
  ConstraintsRisks: "Constraints, risks, or considerations?",
};

// Every required field, in form order — used to size the completeness
// progress indicator so it reflects the whole form, not just the AI-judged
// subset.
const ALL_REQUIRED_FIELDS: (keyof FormState)[] = [
  "Title",
  "Yourname",
  "Team",
  "Region",
  "Problem",
  "Frequency",
  "ImpactedToday",
  "IfSolved",
  "AnticipatedImpact",
  "ImpactType",
  "ProposedSolution",
  "ExistingExamples",
  "DataInputs",
  "ConstraintsRisks",
];

// New submissions land as "New" (the Initiative list's real default status —
// "Pending" is not a valid choice on this list).
const LOCKED_STATUS = "New";
const AUTO_CLOSE_MS = 2400;

// Fallback choice values, used only until the live column definitions load
// from SharePoint (or if that fetch fails). Region, How-often, and
// Type-of-impact are closed choice sets (allowTextEntry: false), so free text
// would be rejected on save.
const REGION_CHOICES_FALLBACK = [
  "Atlanta",
  "Austin",
  "Corporate",
  "Calgary",
  "Edmonton",
  "NorCal",
  "Washington",
  "Denver",
  "Toronto",
  "Phoenix",
  "SoCal",
  "Houston",
  "Raleigh / Durham",
  "Charleston",
  "Tampa",
  "Wilmington",
  "Dallas",
  "Portland",
  "Seattle",
  "Charlotte",
  "Spokane",
  "Sacramento",
  "Columbus",
  "New Jersey",
  "Delaware",
  "Virginia",
  "Puget Sound",
  "Weymouth",
];

const FREQUENCY_CHOICES_FALLBACK = [
  "Daily",
  "Weekly",
  "Monthly",
  "Occasionally",
  "One-time, but high impact",
];

const IMPACT_TYPE_CHOICES_FALLBACK = [
  "Time savings / efficiency",
  "Cost reduction (material, licensing, budget, etc.)",
  "Improved insights and decision quality",
  "Risk reduction",
  "Scalability",
  "Better customer or partner experience",
  "Other",
];

function uniqueValues(
  items: InitiativeItem[],
  pick: (i: InitiativeItem) => string
): string[] {
  const set = new Set<string>();
  for (const item of items) {
    const v = pick(item).trim();
    if (v) set.add(v);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function AddInitiativeItemModal({ existing, onClose, onCreated, onTryChat }: Props) {
  const getToken = useGraphToken();
  const { review } = useInitiativeAssist();
  const { profile } = useProfile({ skipPhoto: true });
  const { columns } = useListColumns(INITIATIVE_LIST_ID);
  const regionChoices = useMemo(
    () => choicesFor(columns, "Region_x002f_Division", REGION_CHOICES_FALLBACK),
    [columns]
  );
  const frequencyChoices = useMemo(
    () => choicesFor(columns, "Howoftendoesthisproblemoccur_x00", FREQUENCY_CHOICES_FALLBACK),
    [columns]
  );
  const impactTypeChoices = useMemo(
    () => choicesFor(columns, "Whattypeofimpacttodoyouexpect_x0", IMPACT_TYPE_CHOICES_FALLBACK),
    [columns]
  );
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  // What "unedited" looks like — starts as EMPTY, but the profile-prefill
  // effect below updates it too, so auto-filled values don't themselves
  // count as unsaved edits (see `dirty`).
  const [baseline, setBaseline] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // AI completeness review — runs once as part of clicking Submit, judging
  // all substantive fields together. Tells the user what's missing for an
  // accurate business/technical review; never rewrites their answer, and
  // never blocks on its own failure (a down assistant shouldn't brick
  // submissions — see the catch block in handleSubmit).
  const [reviewing, setReviewing] = useState(false);
  const [evaluations, setEvaluations] = useState<
    Partial<Record<keyof FormState, FieldEvaluation>>
  >({});
  const [assistError, setAssistError] = useState<string | null>(null);

  // Team / function is still free-text on the list, so we keep suggesting
  // previously-used values. Region, How-often, and Type-of-impact are now
  // fixed choice sets and no longer derive options from existing items.
  const teams = useMemo(
    () => uniqueValues(existing, (i) => i.team),
    [existing]
  );

  // Prefill from the signed-in user's Graph profile: name, team (from
  // department), and region (matched against the closed choice list via
  // officeLocation — only applied if it actually matches one of the valid
  // options, since Region is a closed SharePoint choice column). Runs once
  // per modal open and never overwrites a field the user already touched.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current || !profile) return;
    prefilledRef.current = true;
    setForm((prev) => {
      const next = { ...prev };
      if (!next.Yourname && profile.displayName) next.Yourname = profile.displayName;
      if (!next.Team && profile.department) next.Team = profile.department;
      if (!next.Region && profile.officeLocation) {
        const match = regionChoices.find(
          (r) => r.trim().toLowerCase() === profile.officeLocation!.trim().toLowerCase()
        );
        if (match) next.Region = match;
      }
      setBaseline(next);
      return next;
    });
  }, [profile, regionChoices]);

  // Unsaved work exists while the form differs from its prefilled starting
  // state and hasn't been submitted yet — so name/team/region auto-filled
  // from the user's profile don't themselves count as an edit. We compare a
  // serialized snapshot so any typed field, including the optional ones and
  // the impact-type checkboxes, counts.
  const dirty = !submitted && JSON.stringify(form) !== JSON.stringify(baseline);

  // Guarded dismiss: prompt before discarding unsaved edits. A save in progress
  // blocks dismissal entirely (matches the existing behavior).
  const requestClose = useCallback(() => {
    if (submitting) return;
    if (dirty) setConfirmOpen(true);
    else onClose();
  }, [submitting, dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting && !confirmOpen) requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose, submitting, confirmOpen]);

  useEffect(() => {
    if (!submitted) return;
    const t = window.setTimeout(() => onCreated(), AUTO_CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [submitted, onCreated]);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Any edit invalidates that field's prior substance check.
    setEvaluations((e) => ({ ...e, [key]: undefined }));
  };

  const toggleImpact = (value: string) => {
    setForm((prev) => ({
      ...prev,
      ImpactType: prev.ImpactType.includes(value)
        ? prev.ImpactType.filter((v) => v !== value)
        : [...prev.ImpactType, value],
    }));
  };

  const AI_CHECKED_FIELDS = Object.keys(FIELD_LABELS) as (keyof FormState)[];

  // A field reads "filled" as soon as it has a value; the AI review can only
  // downgrade one of the 8 substantive fields to "needs work" after judging
  // it, never require anything more up front. This is what "as it's being
  // filled out" status is based on — no per-field AI call needed for it.
  const fieldStatus = (key: keyof FormState): FieldStatus => {
    const evalResult = evaluations[key];
    if (evalResult && !evalResult.sufficient) return "warn";
    const value = form[key];
    const filled = Array.isArray(value) ? value.length > 0 : value.trim().length > 0;
    return filled ? "ok" : "neutral";
  };

  const completedCount = ALL_REQUIRED_FIELDS.filter(
    (key) => fieldStatus(key) === "ok"
  ).length;

  const flaggedCount = AI_CHECKED_FIELDS.filter(
    (key) => evaluations[key]?.sufficient === false
  ).length;

  // Mirror the list's required columns so a submit never 400s on a missing
  // mandatory field. This only gates *attempting* Submit — the AI
  // completeness review (in handleSubmit) is the actual detail gate.
  const canAttemptSubmit =
    form.Title.trim().length > 0 &&
    form.Yourname.trim().length > 0 &&
    form.Team.trim().length > 0 &&
    form.Region.trim().length > 0 &&
    form.Frequency.trim().length > 0 &&
    form.ImpactType.length > 0 &&
    AI_CHECKED_FIELDS.every((key) => (form[key] as string).trim().length > 0) &&
    !submitting &&
    !reviewing;

  const doSave = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const token = await getToken();
      const payload: NewInitiativeFields = {
        Title: form.Title.trim(),
        Yourname: form.Yourname.trim() || undefined,
        Yourteam_x002f_function: form.Team.trim() || undefined,
        Region_x002f_Division: form.Region.trim() || undefined,
        Whatproblemareyoutryingtosolve: form.Problem.trim() || undefined,
        Howoftendoesthisproblemoccur_x00: form.Frequency.trim() || undefined,
        Whoisimpactedbythisproblemtoday_:
          form.ImpactedToday.trim() || undefined,
        Ifthisproblemweresolved_x002c_wh: form.IfSolved.trim() || undefined,
        Pleaseexplaintheanticipatedimpac:
          form.AnticipatedImpact.trim() || undefined,
        Whattypeofimpacttodoyouexpect_x0: form.ImpactType.length
          ? form.ImpactType
          : undefined,
        ProposedSolution: form.ProposedSolution.trim() || undefined,
        ExistingExamples: form.ExistingExamples.trim() || undefined,
        DataInputs: form.DataInputs.trim() || undefined,
        ConstraintsRisks: form.ConstraintsRisks.trim() || undefined,
        AnythingElse: form.AnythingElse.trim() || undefined,
        Sponsor: form.Sponsor.trim() || undefined,
        Status: LOCKED_STATUS,
      };
      await createInitiativeItem(token, payload);
      setSubmitting(false);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  // One AI pass over every substantive field, triggered by Submit itself —
  // no per-field button. If anything's flagged, we stop and show it instead
  // of saving; fixing a flagged field clears its flag (see update()) and the
  // next Submit click re-checks everything. A failed/unavailable assistant
  // call fails OPEN (saves anyway) so an outage can't brick submissions —
  // that's a deliberate exception to "forces users" for reliability, not a
  // loophole for skipping the check under normal conditions.
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canAttemptSubmit) return;
    setAssistError(null);
    setReviewing(true);
    try {
      const fields = AI_CHECKED_FIELDS.map((key) => ({
        label: FIELD_LABELS[key]!,
        value: String(form[key] ?? ""),
      }));
      const issues = await review(form.Title.trim(), fields, [FIELD_LABELS.Problem!]);
      // Normalize both sides before matching — the AI is asked to echo the
      // field label back verbatim, but any drift (paraphrasing, whitespace,
      // casing) would silently break an exact-string match, leaving nothing
      // visibly flagged while still blocking the save. That decoupling was
      // a real bug: gate on whether we could actually attribute an issue to
      // a known field, not on the raw (unmatched) issues array.
      const norm = (s: string) => s.trim().toLowerCase();
      const issuesByLabel = new Map(issues.map((i) => [norm(i.label), i.suggestion]));
      let anyFlagged = false;
      const nextEvaluations: Partial<Record<keyof FormState, FieldEvaluation>> = {};
      for (const key of AI_CHECKED_FIELDS) {
        const label = FIELD_LABELS[key]!;
        const suggestion = issuesByLabel.get(norm(label));
        if (suggestion) anyFlagged = true;
        nextEvaluations[key] = suggestion
          ? { sufficient: false, feedback: suggestion }
          : { sufficient: true, feedback: "" };
      }
      setEvaluations(nextEvaluations);
      setReviewing(false);
      if (anyFlagged) {
        formRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (issues.length > 0) {
        // The AI flagged something but none of it mapped to a known field
        // (a label-matching miss) — don't block silently with no visible
        // reason. Fail open, same as an outright API error would.
        console.warn(
          "initiative review returned issues that didn't match any tracked field:",
          issues
        );
      }
    } catch (err) {
      setReviewing(false);
      setAssistError(err instanceof Error ? err.message : String(err));
    }
    await doSave();
  };

  return (
    <>
      <div
        className="modal-backdrop"
        onClick={(e) => {
          if (e.target === e.currentTarget && !submitting) requestClose();
        }}
      >
        <div
          className="modal modal-initiative add-item-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-accent" aria-hidden />
          <div className="modal-head">
            <div className="modal-head-icon icon-initiative" aria-hidden>
              <Lightbulb size={22} />
            </div>
            <div className="modal-head-text">
              <span className="modal-kind">AI Initiative</span>
              <h2 className="modal-title">
                {submitted ? "Submission received" : "Submit a new initiative"}
              </h2>
              {!submitted ? (
                <p className="add-item-subtitle">
                  Pitch a problem worth solving. Submissions are added as{" "}
                  <strong>New</strong> and reviewed before they're picked up.
                </p>
              ) : null}
              {!submitted && onTryChat ? (
                <button type="button" className="add-item-try-chat" onClick={onTryChat}>
                  <Sparkles size={12} /> Try the new conversational experience
                </button>
              ) : null}
            </div>
            {!submitted ? (
              <button
                type="button"
                className="modal-close"
                onClick={requestClose}
                disabled={submitting}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            ) : null}
          </div>

          {submitted ? (
            <div className="add-item-success" role="status" aria-live="polite">
              <div className="add-item-success-icon" aria-hidden>
                <CheckCircle2 size={40} strokeWidth={2.2} />
              </div>
              <h3 className="add-item-success-title">
                Thanks — your initiative is in the queue
              </h3>
              <p className="add-item-success-body">
                Your initiative submission was saved successfully. A reviewer will
                check it over and publish it to AI Initiatives shortly.
              </p>
              <button
                type="button"
                className="btn btn-primary add-item-success-done"
                onClick={onCreated}
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <form
                className="modal-body add-item-form"
                onSubmit={handleSubmit}
                ref={formRef}
              >
                {flaggedCount > 0 ? (
                  <div className="add-item-review-banner" role="alert">
                    <AlertCircle size={16} aria-hidden />
                    <span>
                      <strong>
                        {flaggedCount} question{flaggedCount > 1 ? "s" : ""} need
                        {flaggedCount > 1 ? "" : "s"} a correction
                      </strong>{" "}
                      before this can be submitted — see the notes under each
                      highlighted field below.
                    </span>
                  </div>
                ) : null}

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <User size={14} aria-hidden />
                    <h3>Who you are</h3>
                  </header>
                  <p className="add-item-section-sub">
                    So reviewers know who to follow up with and what part of the
                    business this is coming from.
                  </p>

                  <Field
                    label="Initiative title"
                    required
                    status={fieldStatus("Title")}
                  >
                    <input
                      type="text"
                      value={form.Title}
                      onChange={(e) => update("Title", e.target.value)}
                      required
                      maxLength={255}
                      autoFocus
                      placeholder={hintFor(columns, "Title", "What would you call this initiative?")}
                    />
                  </Field>

                  <div className="add-item-row">
                    <Field
                      label="Your name"
                      required
                      status={fieldStatus("Yourname")}
                    >
                      <input
                        type="text"
                        value={form.Yourname}
                        onChange={(e) => update("Yourname", e.target.value)}
                        placeholder={hintFor(columns, "Yourname", "First and last name")}
                      />
                    </Field>
                    <Field
                      label="Team / function"
                      required
                      status={fieldStatus("Team")}
                    >
                      <ComboBoxInput
                        value={form.Team}
                        onChange={(v) => update("Team", v)}
                        options={teams}
                        placeholder={hintFor(columns, "Yourteam_x002f_function", "Construction, Finance, HR…")}
                      />
                    </Field>
                  </div>

                  <Field
                    label="Region / division"
                    required
                    status={fieldStatus("Region")}
                  >
                    <div className="add-item-datalist">
                      <select
                        value={form.Region}
                        onChange={(e) => update("Region", e.target.value)}
                        required
                      >
                        <option value="" disabled>
                          Select a region…
                        </option>
                        {regionChoices.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="add-item-datalist-chevron" aria-hidden />
                    </div>
                  </Field>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <AlertCircle size={14} aria-hidden />
                    <h3>The problem</h3>
                  </header>
                  <p className="add-item-section-sub">
                    Help reviewers understand the pain point you're trying to
                    solve and who feels it.
                  </p>

                  <Field
                    label="What problem are you trying to solve?"
                    required
                    status={fieldStatus("Problem")}
                  >
                    <textarea
                      rows={3}
                      value={form.Problem}
                      onChange={(e) => update("Problem", e.target.value)}
                      placeholder={hintFor(columns, "Whatproblemareyoutryingtosolve", "What's the issue, and why does it matter?")}
                    />
                    <FieldFeedback result={evaluations.Problem} />
                  </Field>

                  <div className="add-item-row">
                    <Field
                      label="How often does it happen?"
                      required
                      status={fieldStatus("Frequency")}
                    >
                      <div className="add-item-datalist">
                        <select
                          value={form.Frequency}
                          onChange={(e) => update("Frequency", e.target.value)}
                          required
                        >
                          <option value="" disabled>
                            Select frequency…
                          </option>
                          {frequencyChoices.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="add-item-datalist-chevron" aria-hidden />
                      </div>
                    </Field>
                    <Field
                      label="Who's impacted? Who would benefit?"
                      required
                      status={fieldStatus("ImpactedToday")}
                    >
                      <input
                        type="text"
                        value={form.ImpactedToday}
                        onChange={(e) =>
                          update("ImpactedToday", e.target.value)
                        }
                        placeholder={hintFor(columns, "Whoisimpactedbythisproblemtoday_", "Site supers, accounting team, buyers…")}
                      />
                      <FieldFeedback result={evaluations.ImpactedToday} />
                    </Field>
                  </div>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <Sparkles size={14} aria-hidden />
                    <h3>If it were solved</h3>
                  </header>
                  <p className="add-item-section-sub">
                    Paint the picture of what changes — this helps reviewers
                    weigh value vs effort.
                  </p>

                  <Field
                    label="If this problem were solved, what would success look like?"
                    required
                    status={fieldStatus("IfSolved")}
                  >
                    <textarea
                      rows={3}
                      value={form.IfSolved}
                      onChange={(e) => update("IfSolved", e.target.value)}
                      placeholder={hintFor(columns, "Ifthisproblemweresolved_x002c_wh", "If we fix this, what does daily life look like?")}
                    />
                    <FieldFeedback result={evaluations.IfSolved} />
                  </Field>

                  <Field
                    label="Anticipated impact"
                    required
                    status={fieldStatus("AnticipatedImpact")}
                  >
                    <textarea
                      rows={2}
                      value={form.AnticipatedImpact}
                      onChange={(e) =>
                        update("AnticipatedImpact", e.target.value)
                      }
                      placeholder={hintFor(
                        columns,
                        "Pleaseexplaintheanticipatedimpac",
                        "e.g. ~4 hrs/week saved per site super; faster month-end close."
                      )}
                    />
                    <FieldFeedback result={evaluations.AnticipatedImpact} />
                  </Field>

                  <Field
                    label="What type of impact do you expect?"
                    required
                    status={fieldStatus("ImpactType")}
                  >
                    <div
                      className="add-item-checkgroup"
                      role="group"
                      aria-label="What type of impact do you expect?"
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      {impactTypeChoices.map((opt) => {
                        const checked = form.ImpactType.includes(opt);
                        return (
                          <label
                            key={opt}
                            className={`add-item-check${
                              checked ? " is-checked" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleImpact(opt)}
                            />
                            <span>{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  </Field>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <Tag size={14} aria-hidden />
                    <h3>Proposed solution</h3>
                  </header>
                  <p className="add-item-section-sub">
                    Share your thinking on a solution and what it would take.
                  </p>

                  <Field
                    label="Do you have a proposed AI solution in mind?"
                    required
                    status={fieldStatus("ProposedSolution")}
                  >
                    <textarea
                      rows={3}
                      value={form.ProposedSolution}
                      onChange={(e) =>
                        update("ProposedSolution", e.target.value)
                      }
                      placeholder={hintFor(columns, "Ifyes_x002c_brieflydescribethepr", "What do you think would solve it?")}
                    />
                    <FieldFeedback result={evaluations.ProposedSolution} />
                  </Field>

                  <Field
                    label="Are there existing examples we could mirror or learn from?"
                    required
                    status={fieldStatus("ExistingExamples")}
                  >
                    <textarea
                      rows={2}
                      value={form.ExistingExamples}
                      onChange={(e) =>
                        update("ExistingExamples", e.target.value)
                      }
                      placeholder={hintFor(columns, "Arethereexistingexampleswecouldm", "Links, products, or teams already doing this.")}
                    />
                    <FieldFeedback result={evaluations.ExistingExamples} />
                  </Field>

                  <div className="add-item-row">
                    <Field
                      label="What data or inputs would be involved?"
                      required
                      status={fieldStatus("DataInputs")}
                    >
                      <textarea
                        rows={2}
                        value={form.DataInputs}
                        onChange={(e) => update("DataInputs", e.target.value)}
                        placeholder={hintFor(columns, "Whatdataorinputswouldbeinvolved_", "Reports, spreadsheets, systems, files…")}
                      />
                      <FieldFeedback result={evaluations.DataInputs} />
                    </Field>
                    <Field
                      label="Constraints, risks, or considerations?"
                      required
                      status={fieldStatus("ConstraintsRisks")}
                    >
                      <textarea
                        rows={2}
                        value={form.ConstraintsRisks}
                        onChange={(e) =>
                          update("ConstraintsRisks", e.target.value)
                        }
                        placeholder={hintFor(columns, "Arethereanyconstraints_x002c_ris", "Guardrails, blockers, or things to watch.")}
                      />
                      <FieldFeedback result={evaluations.ConstraintsRisks} />
                    </Field>
                  </div>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <Sparkles size={14} aria-hidden />
                    <h3>Anything else (optional)</h3>
                  </header>
                  <p className="add-item-section-sub">
                    Optional context that helps reviewers — a sponsor or any
                    closing notes.
                  </p>

                  <Field
                    label="Is there a sponsor for this initiative? If so, who?"
                  >
                    <textarea
                      rows={2}
                      value={form.Sponsor}
                      onChange={(e) => update("Sponsor", e.target.value)}
                      placeholder={hintFor(columns, "Isthereasponsorforthisinitiative", "Name or team sponsoring this initiative.")}
                    />
                  </Field>

                  <Field
                    label="Anything else we should know?"
                  >
                    <textarea
                      rows={2}
                      value={form.AnythingElse}
                      onChange={(e) => update("AnythingElse", e.target.value)}
                      placeholder={hintFor(columns, "Anythingelseweshouldknow_x003f_", "Anything that didn't fit above.")}
                    />
                  </Field>
                </section>

                {assistError ? (
                  <div className="add-item-error" role="alert">
                    <strong>AI assistant unavailable.</strong> {assistError}
                  </div>
                ) : null}

                {error ? (
                  <div className="add-item-error" role="alert">
                    <strong>Couldn't save.</strong> {error}
                  </div>
                ) : null}
              </form>

              <div className="modal-foot">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <div className="add-item-submit-group">
                  {!submitting ? (
                    <div className="add-item-gate" title="Required fields completed">
                      <ListChecks size={13} aria-hidden />
                      <div className="add-item-gate-track">
                        <div
                          className="add-item-gate-fill"
                          style={{
                            width: `${(completedCount / ALL_REQUIRED_FIELDS.length) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="add-item-gate-count">
                        {completedCount}/{ALL_REQUIRED_FIELDS.length}
                      </span>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!canAttemptSubmit}
                    onClick={handleSubmit}
                  >
                    {reviewing ? (
                      <>
                        <Loader2 size={14} className="add-item-spin" /> Checking detail…
                      </>
                    ) : submitting ? (
                      <>
                        <Loader2 size={14} className="add-item-spin" /> Saving…
                      </>
                    ) : (
                      "Submit for review"
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Discard your changes?"
        message="You've started filling this out but haven't submitted yet. If you leave now, your entries will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        tone="danger"
        autoFocusConfirm={false}
        onConfirm={() => {
          setConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

type FieldStatus = "neutral" | "ok" | "warn";

function Field({
  label,
  required,
  hint,
  status = "neutral",
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  status?: FieldStatus;
  children: ReactNode;
}) {
  return (
    <label className={`add-item-field is-status-${status}`}>
      <span className="add-item-label">
        {label}
        {required ? (
          <span aria-hidden className="add-item-required">
            *
          </span>
        ) : null}
        {status === "ok" ? (
          <CheckCircle2 size={13} className="add-item-status-icon" aria-hidden />
        ) : null}
      </span>
      {children}
      {hint ? <span className="add-item-hint">{hint}</span> : null}
    </label>
  );
}

// Shown only for a field the submit-time AI review flagged as insufficient —
// editing the field clears it (see update()) until the next review pass.
function FieldFeedback({ result }: { result?: FieldEvaluation }) {
  if (!result || result.sufficient) return null;
  return (
    <div className="add-item-suggestion">
      <p>{result.feedback}</p>
    </div>
  );
}

// Custom combobox instead of a native <input list> — browsers render the
// native <datalist> suggestion popup with their own unstyleable UI (often a
// dark autofill-style list), which looks jarringly different from our
// styled <select> dropdowns. This renders its own listbox so both match.
function ComboBoxInput({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, value]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const choose = (opt: string) => {
    onChange(opt);
    setOpen(false);
  };

  return (
    <div className="add-item-combobox" ref={containerRef}>
      <div className="add-item-datalist">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setHighlighted(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlighted((h) => Math.max(h - 1, 0));
            } else if (e.key === "Enter" && open && filtered[highlighted]) {
              e.preventDefault();
              choose(filtered[highlighted]);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {options.length ? (
          <ChevronDown
            size={14}
            className="add-item-datalist-chevron"
            aria-hidden
            onClick={() => setOpen((o) => !o)}
          />
        ) : null}
      </div>
      {open && filtered.length > 0 ? (
        <ul className="add-item-combobox-list" role="listbox">
          {filtered.map((opt, i) => (
            <li
              key={opt}
              role="option"
              aria-selected={opt === value}
              className={`add-item-combobox-option${i === highlighted ? " is-highlighted" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(opt)}
              onMouseEnter={() => setHighlighted(i)}
            >
              {opt}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
